import { ComponentPropsWithoutRef, FC, HTMLAttributes, ReactNode } from 'react'
import styled from 'styled-components'

import {
  Carat as LMFCarat,
  StyledCheckbox,
  TypeStyle,
  Warning,
  fromMd,
} from '@syconium/little-miss-figgy'

import { EmbroideryFormKind } from '../../../../containers/EmbroideryService/types'

/**
 * Embroidery option selector list wrapper.
 */
export const List: FC<HTMLAttributes<HTMLUListElement>> = ({ children, ...rest }) => {
  return <ul {...rest}>{children}</ul>
}

/**
 * Embroidery option selector list item.
 *
 *
 * @param props
 * @param props.index - The index of the given option.
 * @param props.handleClick - Callback function for when the option is clicked.
 */
export type EmbroideryOptionProps = {
  index: number
  disabled?: boolean
  kind: EmbroideryFormKind
  handleClick: (kind: EmbroideryFormKind) => void
  label?: string
  error?: ReactNode
  errorMessageId?: string
} & HTMLAttributes<HTMLLIElement>

export const ListOption: FC<EmbroideryOptionProps> = ({
  children,
  index,
  kind,
  label,
  handleClick,
  disabled,
  error,
  errorMessageId,
  ...rest
}) => {
  return (
    <ListItem key={index} {...rest}>
      <ListButton
        disabled={disabled}
        aria-label={label}
        aria-describedby={errorMessageId}
        aria-invalid={Boolean(error)}
        onClick={() => handleClick(kind)}
      >
        <ListItemContent>{children}</ListItemContent>
        {error && <ListItemError>{error}</ListItemError>}
      </ListButton>
    </ListItem>
  )
}

/**
 * A checkbox for the embroidery option selector.
 *
 * @param props
 * @param props.checked - State of the option defaults to false.
 */
export type EmbroideryCheckboxProps = {
  checked: boolean
  children: ReactNode
  disabled?: boolean
}

export const Checkbox: FC<EmbroideryCheckboxProps> = ({ children, checked = false, disabled }) => {
  return (
    <CheckboxWrap>
      <CheckboxIcon
        data-testid='embroidery-option-styled-checkbox'
        data-checked={checked}
        disabled={disabled}
        checked={checked}
      />
      {children}
    </CheckboxWrap>
  )
}

/**
 * The price of the embroidery option.
 */
export const Price: FC<{ disabled?: boolean; children: ReactNode }> = ({ children, disabled }) => {
  return (
    <PriceWrap disabled={disabled}>
      {children}
      <Carat />
    </PriceWrap>
  )
}

/**
 * An error message that should be displayed alongside an invalid embroidery option.
 */
export const Error: FC<
  { message?: string; id: string } & Omit<ComponentPropsWithoutRef<typeof ErrorMessage>, 'children'>
> = ({ id, message, ...rest }) => {
  return (
    <ErrorMessage id={id} {...rest}>
      <ErrorIcon width='16px' height='16px' /> {message}
    </ErrorMessage>
  )
}

export const EmbroideryOption = {
  Error,
  List,
  Checkbox,
  Price,
  ListOption,
}

const ListItem = styled.li`
  ${fromMd} {
    &:first-child {
      button {
        padding-top: ${o => o.theme.spacing(5)};
      }
    }
  }
`

const ErrorMessage = styled(TypeStyle.BodyNano).attrs({ asTag: 'span' })`
  font-weight: 600;
  color: ${o => o.theme.color.deprecated.red.error};
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
`

const ErrorIcon = styled(Warning).attrs(o => ({ fill: o.theme.color.deprecated.red.error }))``

const ListButton = styled.button`
  ${TypeStyle.css.headlineMicro}
  width: 100%;
  padding: ${o => o.theme.spacing(5)} 0;
  border-bottom: 1px solid ${o => o.theme.color.deprecated.stoneLight};

  ${fromMd} {
    padding: ${o => o.theme.spacing(5)} 0;
  }

  &:hover {
    cursor: pointer;
  }

  &:disabled {
    cursor: not-allowed;
  }

  ${ListItem}:first-child & {
    border-top: 1px solid ${o => o.theme.color.deprecated.stoneLight};
  }
`

export const ListItemContent = styled.div`
  display: flex;
  justify-content: space-between;
`
export const ListItemError = styled.div`
  margin-top: ${o => o.theme.spacing(2)};
`

const Carat = styled(LMFCarat)`
  padding-left: ${o => o.theme.spacing(2)};
`
const CheckboxWrap = styled.span`
  display: flex;
  text-transform: uppercase;
`

const CheckboxIcon = styled(StyledCheckbox.Checkbox)`
  top: 0px;
  > svg {
    width: 16px;
    height: 16px;
  }
`

const PriceWrap = styled.div<{ disabled?: boolean }>`
  ${TypeStyle.css.headlineMicro}
  color: ${o =>
    o.disabled ? o.theme.color.deprecated.stoneLighter : o.theme.color.deprecated.obsidian};
  font-weight: ${o => o.theme.typography.font.weight[700]};
`
